import {
  HttpInterceptorFn,
  HttpHandlerFn,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, retry, throwError, timer } from 'rxjs';
import { NotificationService } from '../services/notification.service';

export interface ErrorDetails {
  message: string;
  statusCode: number;
  statusText: string;
  url: string;
  timestamp: Date;
  correlationId?: string;
  details?: any;
}

export const errorInterceptorFn: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const notification = inject(NotificationService);
  const router = inject(Router);

  const generateCorrelationId = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const getErrorMessage = (status: number): string => {
    const messages: { [key: number]: string } = {
      0: 'Network error occurred',
      400: 'Bad request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not found',
      409: 'Conflict',
      422: 'Validation failed',
      429: 'Too many requests',
      500: 'Internal server error',
      502: 'Bad gateway',
      503: 'Service unavailable',
      504: 'Gateway timeout',
    };

    return messages[status] || 'An error occurred';
  };

  const buildErrorDetails = (error: HttpErrorResponse): ErrorDetails => {
    let message = 'An unexpected error occurred';

    if (error.error instanceof ErrorEvent) {
      message = error.error.message;
    } else if (error.error?.message) {
      message = error.error.message;
    } else {
      message = getErrorMessage(error.status);
    }

    return {
      message,
      statusCode: error.status,
      statusText: error.statusText,
      url: req.url,
      timestamp: new Date(),
      correlationId: generateCorrelationId(),
      details: error.error,
    };
  };

  const handleValidationErrors = (errorDetails: ErrorDetails): void => {
    if (errorDetails.details?.errors) {
      const errors = errorDetails.details.errors;
      if (Array.isArray(errors)) {
        errors.forEach((error) => {
          notification.error(error.message || error);
        });
      } else if (typeof errors === 'object') {
        Object.values(errors).forEach((error: any) => {
          if (Array.isArray(error)) {
            error.forEach((e) => notification.error(e));
          } else {
            notification.error(error);
          }
        });
      }
    } else {
      notification.error('Validation failed. Please check your input.');
    }
  };

  const handleError = (errorDetails: ErrorDetails): void => {
    // Handle specific error codes
    switch (errorDetails.statusCode) {
      case 0:
        notification.error('Network error. Please check your connection.');
        break;
      case 400:
        notification.error(errorDetails.message || 'Invalid request. Please check your input.');
        break;
      case 401:
        // Handled by auth interceptor
        break;
      case 403:
        notification.error('You do not have permission to perform this action.');
        break;
      case 404:
        notification.error('The requested resource was not found.');
        break;
      case 409:
        notification.error(
          errorDetails.message || 'Conflict detected. Please refresh and try again.'
        );
        break;
      case 422:
        handleValidationErrors(errorDetails);
        break;
      case 429:
        notification.error('Too many requests. Please try again later.');
        break;
      case 500:
        notification.error('Internal server error. Please try again later.');
        router.navigate(['/error/server-error']);
        break;
      case 502:
        notification.error('Bad gateway. The server is temporarily unavailable.');
        break;
      case 503:
        notification.error('Service unavailable. Please try again later.');
        router.navigate(['/error/maintenance']);
        break;
      case 504:
        notification.error('Gateway timeout. The request took too long to process.');
        break;
      default:
        if (errorDetails.statusCode >= 500) {
          notification.error('Server error. Please try again later.');
        } else if (errorDetails.statusCode >= 400) {
          notification.error(errorDetails.message);
        }
    }
  };

  // Determine retry count
  const shouldRetry = req.method === 'GET' && !req.url.includes('/auth/');
  const retryCount = shouldRetry ? 2 : 0;

  return next(req).pipe(
    retry({
      count: retryCount,
      delay: (error, count) => {
        if (error.status === 0 || error.status === 503 || error.status === 504) {
          const delay = Math.min(1000 * Math.pow(2, count - 1), 10000);
          return timer(delay);
        }
        return throwError(() => error);
      },
    }),
    catchError((error: HttpErrorResponse) => {
      const errorDetails = buildErrorDetails(error);
      handleError(errorDetails);
      return throwError(() => errorDetails);
    })
  );
};
